/**
 * Bids Page (Provider)
 * Shows provider's own bids
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useLanguage, API } from "@/context/AppContext";
import { Briefcase, Loader2 } from "lucide-react";

const BidsPage = () => {
  const { t } = useLanguage();
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/bids/my`, { withCredentials: true })
      .then(res => setBids(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#0044CC]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bids-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
          {t('myBids')}
        </h1>
        <p className="text-gray-500 mt-1">{t('bidsOverview')}</p>
      </div>

      {bids.length === 0 ? (
        <Card className="border-gray-200">
          <CardContent className="p-12 text-center">
            <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">{t('noBids')}</p>
            <Button 
              onClick={() => navigate('/dashboard/jobs')}
              className="mt-4 bg-[#0044CC] hover:bg-[#003399] text-white rounded-full"
            >
              {t('browseJobsBtn')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {bids.map((bid) => (
            <Card 
              key={bid.bid_id} 
              className="border-gray-200 hover:border-[#0044CC]/30 cursor-pointer transition-colors"
              onClick={() => navigate(`/dashboard/jobs/${bid.job_id}`)}
            >
              <CardContent className="p-5">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold">{bid.job_title || 'Auftrag'}</h3>
                    <p className="text-gray-500 text-sm line-clamp-1 mt-1">{bid.message}</p>
                    <p className="text-xs text-gray-400 mt-2">
                      {new Date(bid.created_at).toLocaleDateString('de-CH')}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-[#0044CC]">CHF {bid.amount}</p>
                    <Badge className={`mt-2 text-xs ${
                      bid.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      bid.status === 'accepted' ? 'bg-green-100 text-green-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {bid.status === 'pending' && t('pending')}
                      {bid.status === 'accepted' && t('accepted')}
                      {bid.status === 'rejected' && t('rejected')}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default BidsPage;
